import React from 'react';

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';

import { useHistory } from 'react-router-dom';

// core components
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardFooter from 'components/Card/CardFooter';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CustomInput from 'components/CustomInput/CustomInput';
import Button from 'components/CustomButtons/Button';

import { startPasswordReset, confirmPasswordReset } from 'services/user.service';
import { useSnackbar } from 'notistack';
import SnackMessage from 'components/Snackbar/SnackMessage';

import layoutNames from 'utils/enums/layout-names.enum';
import styles from 'assets/jss/material-dashboard-pro-react/views/loginPageStyle';

const useStyles = makeStyles(styles);

const isInputEmail = (input) => {
	if (input) {
		const re = /\S+@\S+\.\S+/;
		return re.test(input);
	}

	return false;
}

// The page consists of two states:
//     1. User must enter their name or email address and submit it to generate an OTP code, which should be sent to their email
//     2. Once the OTP code is sent, the user must enter that code and the new password and submit both to trigger the reset
// There should be two different views:
//     1. Initially the user must be prompted for their name or email address
//     2. Once the OTP code is generated, the user must be prompted for the OTP code and the new password
export default function UserPasswordResetPage(props) {
	const history = useHistory();

	const classes = useStyles();
	// Flag for which state the page is in i.e. show view #1 or view #2
	const [otpSent, setotpSent] = React.useState(false);
	// View #1 values
	const [userInput, setuserInput] = React.useState('');
	const [userInputState, setuserInputState] = React.useState('');
	// View #2 values
	const [otpInput, setotpInput] = React.useState('');
	const [otpInputState, setotpInputState] = React.useState('');
	const [passwordInput, setpasswordInput] = React.useState('');
	const [passwordInputState, setpasswordInputState] = React.useState('');
	const { enqueueSnackbar } = useSnackbar();


	const onGenerateOTP = async (event) => {
		try {
			const email = isInputEmail(userInput) ? userInput : null;
			const username = isInputEmail(userInput) ? null : userInput;
			if (email || username) {
				const result = await startPasswordReset(email, username);
				console.log('{UserPasswordResetPage} - onGenerateOTP received result from startPasswordReset ==> ' + JSON.stringify(result))

				if (result && result.success) {
					// Prompt for OTP on next render
					setotpSent(true);

					enqueueSnackbar(['Please check your email for the confirmation code. You will need it to complete the reset.'], {
						content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
					});
				} else {
					enqueueSnackbar(['Something went wrong. Please try again or contact the Administrator.'], {
						content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
					});
				}
			}
		} catch (error) {
			console.error('{UserPasswordResetPage} - onGenerateOTP caught error ==> ' + JSON.stringify(error));
			enqueueSnackbar(error.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	}

	const onSubmitOTP = async (event) => {
		try {
			const otp = otpInput.trim();
			const newPassword = passwordInput.trim();
			if (otp && newPassword) {
				const result = await confirmPasswordReset(otp, newPassword);
				console.log('{UserPasswordResetPage} - onSubmitOTP received result from confirmPasswordReset ==> ' + JSON.stringify(result))

				if  (result && result.success) {
					// Redirect to the login page
					history.push(`/${layoutNames.auth}/login`);

					enqueueSnackbar(['Your password has been reset. Please login with your new password.'], {
						content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
					});
				} else {
					enqueueSnackbar(['Something went wrong. Please try again or contact the Administrator.'], {
						content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
					});
				}
			}
		} catch (error) {
			console.error('{UserPasswordResetPage} - onSubmitOTP caught error ==> ' + JSON.stringify(error));
			console.error(error);
			enqueueSnackbar(error.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	}

	const submitCallback = async (event) => {
		// This is required to prevent the page from being reloaded
		event.preventDefault();
		if (otpSent) {
			await onSubmitOTP(event);
		} else {
			await onGenerateOTP(event);
		}
	}

	const printState = () => {
		console.log('{UserPasswordResetPage} {DUMP} otpSent => ' + otpSent + ', userInput => ' + userInput + ', otpInput => ' + otpInput + ', passwordInput => ' + passwordInput);
	}

	printState();
	return (
		<div className={classes.container}>
			<GridContainer justify='center'>
				<GridItem xs={12} sm={6} md={4}>
					<form onSubmit={submitCallback}>
						<Card>
							<CardHeader color='rose' className={`${classes.inlineBlock} ${classes.textCenter}`}>
								<h4 className={classes.inlineBlock}>Reset Your Password</h4>
							</CardHeader>
							<CardBody>
								{otpSent ? (
									<div>
										<CustomInput
											success={otpInputState === 'success'}
											error={otpInputState === 'error'}
											id='otp'
											labelText='Enter the code sent to your email'
											formControlProps={{
												fullWidth: true
											}}
											inputProps={{
												type: 'password',
												autoComplete: 'off',
											}}
											onChange={(event) => {
												const newValue = event.target.value?.trim();
												if (newValue) {
													setotpInputState('inactive');
												} else {
													setotpInputState('error');
												}
												setotpInput(newValue);
											}}
										/>
										<CustomInput
											success={passwordInputState === 'success'}
											error={passwordInputState === 'error'}
											id='password'
											labelText='Your new password'
											formControlProps={{
												fullWidth: true
											}}
											inputProps={{
												type: 'password',
												autoComplete: 'off'
											}}
											onChange={(event) => {
												const newValue = event.target.value?.trim();
												if (newValue) {
													setpasswordInputState('inactive');
												} else {
													setpasswordInputState('error');
												}
												setpasswordInput(newValue);
											}}
										/>
									</div>
								) : (
									<div>
										<CustomInput
											success={userInputState === 'success'}
											error={userInputState === 'error'}
											id='user'
											labelText='Your name or email'
											formControlProps={{
												fullWidth: true
											}}
											inputProps={{
												type: 'text',
											}}
											onChange={(event) => {
												console.log('{UserPasswordResetPage} {user} - onChange invoked');
												const newValue = event.target.value?.trim();
												console.log('{UserPasswordResetPage} {user} - newValue => ' + newValue);
												if (newValue) {
													setuserInputState('inactive');
												} else {
													setuserInputState('error');
												}
												setuserInput(newValue);
											}}
										/>
									</div>
								)}
							</CardBody>
							<CardFooter className={classes.justifyContentCenter}>
								<Button type='submit' color='rose' size='lg' block>
									SUBMIT
								</Button>
							</CardFooter>
						</Card>
					</form>
				</GridItem>
			</GridContainer>
		</div>
	);
}
